import type { BusMessage, BusPayload } from "./messages";

/**
 * Page-level message bus — the Node-RED "flow" of a playground page.
 *
 * Subscription patterns support MQTT-style wildcards:
 *   `co2/ppm`   exact
 *   `co2/+`     one segment
 *   `co2/#`     any depth (also `#` for everything)
 */

export type BusHandler = (msg: BusMessage) => void;

export function topicMatches(pattern: string, topic: string): boolean {
  if (pattern === "#") return true;
  const p = pattern.split("/");
  const t = topic.split("/");
  for (let i = 0; i < p.length; i++) {
    if (p[i] === "#") return true;
    if (i >= t.length) return false;
    if (p[i] !== "+" && p[i] !== t[i]) return false;
  }
  return p.length === t.length;
}

export class MessageBus {
  private subs = new Map<string, Set<BusHandler>>();
  /** Last message per exact topic — late subscribers get current state. */
  private retained = new Map<string, BusMessage>();

  publish(topic: string, payload: BusPayload, opts?: { src?: string; retain?: boolean }): BusMessage {
    const msg: BusMessage = {
      topic,
      payload,
      device: topic.includes("/") ? topic.slice(0, topic.indexOf("/")) : undefined,
      ts: Date.now(),
      src: opts?.src,
    };
    if (opts?.retain !== false) this.retained.set(topic, msg);
    const deliveries: BusHandler[] = [];
    for (const [pattern, handlers] of this.subs) {
      if (!topicMatches(pattern, topic)) continue;
      deliveries.push(...handlers);
    }
    for (const handler of deliveries) {
      try {
        handler(msg);
      } catch (err) {
        console.error(`[granrojo] bus handler error on ${topic}:`, err);
      }
    }
    return msg;
  }

  /**
   * Subscribe to a topic pattern. Returns an unsubscribe function.
   * If a retained message matches, it is replayed immediately (unless
   * `replay: false`).
   */
  subscribe(pattern: string, handler: BusHandler, opts?: { replay?: boolean }): () => void {
    let set = this.subs.get(pattern);
    if (!set) {
      set = new Set();
      this.subs.set(pattern, set);
    }
    set.add(handler);
    let active = true;
    if (opts?.replay !== false) {
      for (const [topic, msg] of this.retained) {
        if (topicMatches(pattern, topic)) {
          try {
            handler(msg);
          } catch (err) {
            console.error(`[granrojo] bus replay error on ${topic}:`, err);
          }
        }
      }
    }
    return () => {
      if (!active) return;
      active = false;
      set.delete(handler);
      if (set.size === 0) this.subs.delete(pattern);
    };
  }

  lastMessage(topic: string): BusMessage | undefined {
    return this.retained.get(topic);
  }
}
